import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Search, Linkedin, UserPlus, X } from "lucide-react";
import {
  listRfpPrimeContacts,
  listRfpPrimeContactStates,
  listRfpPrimeContactCountries,
  getOpportunityRfpPrimeContacts,
  setRfpPrimeContactHighValue,
  bulkGetPrimeCompanies,
  bulkGetPrimeContactStatus,
  ContractorType,
  type ContractorTypeValue,
  type RfpPrimeContact,
  type RfpPrimeContactFilters,
  type PrimeCompany,
  type PrimeContactStatusInfo,
  type PrimeContact,
} from "./api/CrmApi";
import FlagPrimeContactModal from "./ui/FlagPrimeContactModal";
import LogReplyModal from "./ui/LogReplyModal";
import SetPrimeContactStatusModal from "./ui/SetPrimeContactStatusModal";
import AddPrimeContactModal from "./ui/AddPrimeContactModal";
import PeopleWithoutRfpView from "./PeopleWithoutRfpView";
import Button from "shared/Button";
import PrimeContactStatusDropdown, { type PrimeContactStatus } from "./ui/PrimeContactStatusDropdown";
import HighValueTooltip from "./ui/HighValueTooltip";
import ReplyTooltip from "./ui/ReplyTooltip";
import ContractorTypeBadge from "./ui/ContractorTypeBadge";
import { ensureHttpUrl } from "./ui/crmUi";
import PageHeader from "shared/PageHeader";
import StatusPill from "shared/StatusPill";
import { TONE_SOFT } from "shared/tones";
import Alert from "shared/Alert";
import { FILLED_INPUT, FORM_CONTROL, TABLE_SHELL, TBODY_ROW, TD, TH, THEAD_ROW } from "shared/tableStyles";
import PagerNav from "shared/PagerNav";
import { useUrlState, useUrlNumberState, useUpdateEffect, rowLinkState, useHighlightedRow, rowDomId, ROW_HIGHLIGHT_CLASS, type ListLinkState } from "shared/listNavigation";

// The role dropdown filters on the COMPANY'S status on the contact's own RFP (prime if any of its sheet rows is
// prime), not on the raw category text stored from the sheet: the two can
// disagree, and the row badge shows the same per-RFP status the filter matches.
// Sub-contractors are no longer stored (2026-10-20); a role the sheet did not say is shown as "unconfirmed" (2026-10-22).
const CATEGORY_TYPES: Record<string, ContractorTypeValue> = {
  "Prime Candidate": ContractorType.Prime,
  "Role unconfirmed": ContractorType.Unconfirmed,
};

const CATEGORIES = ["Prime Candidate", "Role unconfirmed"];
const ROLE_LABELS: Record<string, string> = {
  "Prime Candidate": "Company is prime on the RFP",
  "Role unconfirmed": "Role unconfirmed (the sheet did not say)",
};

// The raw sheet category is only worth showing when it says something the type badge doesn't (e.g. "Highly
// Relevant"); "Prime Candidate" / "Subcontractor Candidate" just duplicate the badge — or, when the two disagree,
// contradict it.
const RAW_CATEGORIES_SHOWN_AS_BADGE = new Set(["prime candidate", "subcontractor candidate"]);

// Same rule as the server's plan_holder filter: "Active - Plan Holder" counts; "Not a plan holder" and
// "Plan Holder Removed" do not.
function isPlanHolder(status: string | null | undefined): boolean {
  const text = (status ?? "").trim();
  return /plan holder/i.test(text) && !/^not\b/i.test(text) && !/removed/i.test(text);
}

function AppearanceBadge({ count }: { count?: number }) {
  if (!count || count <= 1) return null;
  return (
    <span
      title={`Appeared as a candidate on ${count} RFPs`}
      className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${TONE_SOFT.brand}`}
    >
      ×{count}
    </span>
  );
}

function NaicsBadge({ company }: { company?: PrimeCompany }) {
  if (!company || company.naics_codes.length === 0) return null;
  const codes = company.naics_codes.join(", ");
  return (
    <span
      title={`Tagged capability lane${company.status !== "active" ? ` (${company.status})` : ""}: NAICS ${codes}${company.notes ? ` — ${company.notes}` : ""}`}
      className={`inline-flex items-center justify-center px-1.5 py-0.5 rounded-md text-[10px] font-mono border ${
        company.status === "active" ? TONE_SOFT.success : TONE_SOFT.neutral
      }`}
    >
      {codes}
    </span>
  );
}

function ContactRow({
  contact,
  company,
  status,
  showRfpColumn,
  crmBasePath,
  linkState,
  highlighted,
  onFlag,
  onUnflag,
  onLogReply,
  onChangeStatus,
}: {
  contact: RfpPrimeContact;
  company?: PrimeCompany;
  status?: PrimeContactStatusInfo;
  showRfpColumn: boolean;
  crmBasePath: string;
  /** Passed as <Link state> so the profile page's Back returns to this exact list URL. */
  linkState: ListLinkState;
  /** True for the row we just came back from (see useHighlightedRow). */
  highlighted?: boolean;
  onFlag: (contact: RfpPrimeContact) => void;
  onUnflag: (contact: RfpPrimeContact) => void;
  onLogReply: (contact: RfpPrimeContact) => void;
  onChangeStatus: (contact: RfpPrimeContact, target: PrimeContactStatus) => void;
}) {
  const name = [contact.first_name, contact.last_name].filter(Boolean).join(" ") || contact.main_contact;
  const isDnc = status?.status === "do_not_contact";
  const isBounced = status?.status === "bounced";
  return (
    <tr id={contact.prime_contact_id ? rowDomId("prime", contact.prime_contact_id) : undefined} className={`${TBODY_ROW} ${highlighted ? ROW_HIGHLIGHT_CLASS : isDnc ? "bg-destructive-soft/40" : isBounced ? "bg-warning-soft/40" : ""}`}>
      <td className={TD}>
        <div className="flex items-center gap-2">
          <HighValueTooltip contact={contact} onFlag={() => onFlag(contact)} onUnflag={() => onUnflag(contact)} />
          <ReplyTooltip contact={contact} onClick={() => onLogReply(contact)} />
          {contact.prime_contact_id ? (
            <Link to={`${crmBasePath}/prime-contacts/${contact.prime_contact_id}`} state={linkState} className="text-sm text-primary hover:underline" title="Open contact profile">
              {name || "—"}
            </Link>
          ) : (
            <span className="text-sm text-foreground">{name || "—"}</span>
          )}
        </div>
      </td>
      <td className={`${TD} text-sm text-foreground`}>
        {contact.prime_company_id ? (
          <Link to={`${crmBasePath}/companies/${contact.prime_company_id}`} state={linkState} className="text-primary hover:underline" title="Open company profile">
            {contact.org_name}
          </Link>
        ) : (
          contact.org_name
        )}
        <AppearanceBadge count={contact.appearance_count} />
        <NaicsBadge company={company} />
        <ContractorTypeBadge type={contact.effective_contractor_type} className="ml-1.5" />
        {contact.category && !RAW_CATEGORIES_SHOWN_AS_BADGE.has(contact.category.trim().toLowerCase()) && (
          <span className="block text-[11px] text-muted-foreground">{contact.category}</span>
        )}
      </td>
      <td className={`${TD} text-sm`}>
        {contact.email ? (
          <a href={`mailto:${contact.email}`} className="text-primary hover:underline">{contact.email}</a>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className={`${TD} text-sm text-foreground whitespace-nowrap`}>{contact.phone || "—"}</td>
      <td className={`${TD} text-sm text-foreground`}>
        {[contact.city, contact.state].filter(Boolean).join(", ") || "—"}
      </td>
      <td className={TD}>
        {contact.personal_linkedin_url ? (
          <a
            href={ensureHttpUrl(contact.personal_linkedin_url)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-info hover:text-info/80"
            title="Open this contact's LinkedIn profile"
          >
            <Linkedin className="w-4 h-4" />
          </a>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className={`${TD} text-sm`}>
        {contact.linkedin_status ? (
          <StatusPill tone="neutral">{contact.linkedin_status}</StatusPill>
        ) : (
          <span className="text-muted-foreground">not contacted</span>
        )}
      </td>
      {showRfpColumn && (
        <td className={`${TD} text-sm`}>
          <Link
            to={`${crmBasePath}?opportunity_id=${contact.opportunity_id}`}
            className="text-muted-foreground hover:text-foreground truncate max-w-[180px] inline-block"
            title={
              (contact.prime_rfp_count ?? 1) > 1
                ? `${contact.opportunity_title} — latest of ${contact.prime_rfp_count} RFPs this person appeared on`
                : contact.opportunity_title
            }
          >
            {contact.opportunity_title || `#${contact.opportunity_id}`}
          </Link>
          {(contact.prime_rfp_count ?? 1) > 1 && (
            <span className="ml-1.5 text-[11px] text-muted-foreground">+{(contact.prime_rfp_count ?? 1) - 1} more</span>
          )}
        </td>
      )}
      <td className={TD}>
        {contact.email ? (
          <PrimeContactStatusDropdown
            status={status?.status ?? "active"}
            onSelect={(target) => onChangeStatus(contact, target)}
            compact
          />
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

/** The per-RFP list by default; `?view=no_rfp` switches to the people who have not appeared on any RFP yet. */
export default function PrimeContactsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";
  const toProfile = (created: { id: number }) => navigate(`${crmBasePath}/prime-contacts/${created.id}`);
  if (searchParams.get("view") === "no_rfp") {
    return <PeopleWithoutRfpView onShowAppearances={() => setSearchParams({})} onAddContact={toProfile} />;
  }
  return <AppearancesView onShowNoRfp={() => setSearchParams({ view: "no_rfp" })} onAddContact={toProfile} />;
}

function AppearancesView({ onShowNoRfp, onAddContact }: { onShowNoRfp: () => void; onAddContact: (created: { id: number }) => void }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const opportunityId = searchParams.get("opportunity_id");
  const location = useLocation();
  // Points at the CRM board/table (not this page) so the "RFP" link actually
  // opens that opportunity's detail modal, instead of just filtering this
  // page down to its contacts.
  const crmBasePath = location.pathname.startsWith("/employee") ? "/employee/crm" : "/admin/crm";

  const [items, setItems] = useState<RfpPrimeContact[]>([]);
  const [opportunityTitle, setOpportunityTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Page + every filter live in the URL (alongside ?opportunity_id) so the
  // Back link from a contact/company page and the browser's back button both
  // return to this exact page and filter combination.
  const [page, setPage] = useUrlNumberState("page", 1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useUrlState("q", "");
  const [searchInput, setSearchInput] = useState(search);
  const [nameSearch, setNameSearch] = useUrlState("name", "");
  const [nameInput, setNameInput] = useState(nameSearch);
  const [categoryParam, setCategory] = useUrlState("category", ""); // any role: unconfirmed companies show with their badge
  // an old link may carry the retired "Subcontractor Candidate" value: treat anything unknown as "any"
  const category = categoryParam in CATEGORY_TYPES ? categoryParam : "";
  const [state, setState] = useUrlState("state", "");
  const [stateOptions, setStateOptions] = useState<string[]>([]);
  const [country, setCountry] = useUrlState("country", ""); // "" = any, "none" = not set, else a country name
  const [countryOptions, setCountryOptions] = useState<string[]>([]);
  const [planHolder, setPlanHolder] = useUrlState<"" | "true" | "false">("plan_holder", "");
  const [hasEmail, setHasEmail] = useUrlState<"" | "true" | "false">("has_email", "");
  const [hasLinkedin, setHasLinkedin] = useUrlState<"" | "true" | "false">("has_linkedin", "");
  const [highValue, setHighValue] = useUrlState<"" | "true" | "false">("high_value", "");
  const [replied, setReplied] = useUrlState<"" | "true" | "false">("replied", "");
  const [addOpen, setAddOpen] = useState(false);
  const [flagTarget, setFlagTarget] = useState<RfpPrimeContact | null>(null);
  const [replyTarget, setReplyTarget] = useState<RfpPrimeContact | null>(null);
  const [statusChange, setStatusChange] = useState<{ contact: RfpPrimeContact; target: PrimeContactStatus } | null>(null);
  const [companiesByEmail, setCompaniesByEmail] = useState<Record<string, PrimeCompany>>({});
  const [statusByEmail, setStatusByEmail] = useState<Record<string, PrimeContactStatusInfo>>({});
  // Bumped after a reply is logged: the saved row may not be the one displayed, so re-fetch the page.
  const [refreshToken, setRefreshToken] = useState(0);
  // Row (by profile id) to highlight when returning from a contact's profile page.
  const highlightPrimeId = useHighlightedRow("prime", !loading && items.length > 0);
  const linkState = rowLinkState(location);

  // Debounce the search input.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput, setSearch]);

  useEffect(() => {
    const t = setTimeout(() => setNameSearch(nameInput), 300);
    return () => clearTimeout(t);
  }, [nameInput, setNameSearch]);

  // Skips the mount run — otherwise it would reset a page restored from the URL.
  useUpdateEffect(() => {
    setPage(1);
  }, [search, nameSearch, category, state, country, planHolder, hasEmail, hasLinkedin, highValue, replied, opportunityId]);

  useEffect(() => {
    setLoading(true);
    setError("");

    if (opportunityId) {
      getOpportunityRfpPrimeContacts(Number(opportunityId))
        .then((rows) => {
          setItems(rows);
          setOpportunityTitle(rows[0]?.opportunity_title ?? null);
          setTotalCount(rows.length);
          setTotalPages(1);
        })
        .catch((e) => setError(e.response?.data?.detail || "Failed to load contacts"))
        .finally(() => setLoading(false));
      return;
    }

    const filters: RfpPrimeContactFilters = {
      page,
      page_size: 25,
      search: search || undefined,
      name_search: nameSearch || undefined,
      state: state || undefined,
      country: country || undefined,
      has_email: hasEmail === "" ? undefined : hasEmail === "true",
      has_linkedin: hasLinkedin === "" ? undefined : hasLinkedin === "true",
      is_high_value: highValue === "" ? undefined : highValue === "true",
      has_replied: replied === "" ? undefined : replied === "true",
      contractor_type: category ? CATEGORY_TYPES[category] : undefined,
      plan_holder: planHolder === "" ? undefined : planHolder === "true",
    };
    listRfpPrimeContacts(filters)
      .then((res) => {
        setItems(res.items);
        setTotalCount(res.total_count);
        setTotalPages(res.total_pages);
        setOpportunityTitle(null);
      })
      .catch((e) => setError(e.response?.data?.detail || "Failed to load contacts"))
      .finally(() => setLoading(false));
  }, [opportunityId, page, search, nameSearch, category, state, country, planHolder, hasEmail, hasLinkedin, highValue, replied, refreshToken]);

  // When viewing one RFP's contacts, apply the other filters client-side —
  // the per-opportunity list is small and unpaginated, so this is cheap and
  // avoids adding an opportunity_id param to the global backend query.
  const displayedItems = useMemo(() => {
    if (!opportunityId) return items;
    return items.filter((c) => {
      if (category && c.effective_contractor_type !== CATEGORY_TYPES[category]) return false;
      // Offices of the contact's COMPANY (the server's rule). Only a legacy contact with NO company falls back to its
      // own row's address; a company with no offices on file matches no state.
      if (state) {
        const wanted = state.toLowerCase();
        const matches = c.prime_company_id
          ? (c.company_states ?? []).some((st) => st.toLowerCase() === wanted)
          : (c.state ?? "").toLowerCase() === wanted;
        if (!matches) return false;
      }
      if (planHolder === "true" && !isPlanHolder(c.plan_holder_status)) return false;
      if (planHolder === "false" && isPlanHolder(c.plan_holder_status)) return false;
      if (hasEmail === "true" && !c.email) return false;
      if (hasEmail === "false" && c.email) return false;
      if (hasLinkedin === "true" && !c.personal_linkedin_url) return false;
      if (hasLinkedin === "false" && c.personal_linkedin_url) return false;
      if (highValue === "true" && !c.is_high_value) return false;
      if (highValue === "false" && c.is_high_value) return false;
      if (replied === "true" && !c.replied_at) return false;
      if (replied === "false" && c.replied_at) return false;
      if (country) {
        if (c.prime_company_id) {
          const offices = c.company_countries ?? [];
          // "none" = a company with no office that carries a country.
          if (country === "none" ? offices.length > 0 : !offices.some((cc) => cc.toLowerCase() === country.toLowerCase())) return false;
        } else {
          const rowCountry = (c.country ?? "").trim().toLowerCase();
          const isUs = ["united states", "usa", "us", "u.s.", "u.s.a."].includes(rowCountry);
          if (country === "none" ? rowCountry !== "" : country === "United States" ? !isUs : rowCountry !== country.toLowerCase()) return false;
        }
      }
      if (search && !c.org_name.toLowerCase().includes(search.toLowerCase())) return false;
      if (nameSearch && ![c.first_name, c.last_name].filter(Boolean).join(" ").toLowerCase().includes(nameSearch.toLowerCase())) return false;
      return true;
    });
  }, [items, opportunityId, category, state, country, planHolder, hasEmail, hasLinkedin, highValue, replied, search, nameSearch]);

  // Shared by both bulk-lookup effects below, so the row-derivation logic
  // can't silently diverge between them.
  const displayedEmails = useMemo(
    () => Array.from(new Set(displayedItems.map((c) => c.email?.trim().toLowerCase()).filter((e): e is string => !!e))),
    [displayedItems]
  );

  // One bulk lookup per visible page instead of one GET per row, so the
  // read-only NaicsBadge can show whether a contact's company already has a
  // tagged capability profile.
  useEffect(() => {
    listRfpPrimeContactCountries()
      .then(setCountryOptions)
      .catch(() => {
        // Non-fatal — the dropdown just offers "any" / "not set".
      });
    listRfpPrimeContactStates()
      .then(setStateOptions)
      .catch(() => {
        // Non-fatal — the dropdown just stays at "All states".
      });
  }, []);

  useEffect(() => {
    if (displayedEmails.length === 0) {
      setCompaniesByEmail({});
      return;
    }
    let cancelled = false;
    bulkGetPrimeCompanies(displayedEmails)
      .then((companies) => {
        if (!cancelled) setCompaniesByEmail(companies);
      })
      .catch(() => {
        // Non-fatal — rows just render without a badge/tagged state.
      });
    return () => {
      cancelled = true;
    };
  }, [displayedEmails]);

  // Same one-bulk-call-per-page pattern as the company lookup above, for the
  // Do Not Contact / Bounced icons and row tint.
  useEffect(() => {
    if (displayedEmails.length === 0) {
      setStatusByEmail({});
      return;
    }
    let cancelled = false;
    bulkGetPrimeContactStatus(displayedEmails)
      .then((statuses) => {
        if (!cancelled) setStatusByEmail(statuses);
      })
      .catch(() => {
        // Non-fatal — rows just render without the flagged indicator.
      });
    return () => {
      cancelled = true;
    };
  }, [displayedEmails]);

  // A reply may live on a different RFP's row than the one shown (see
  // RfpPrimeContact.reply_contact_id): act on the row that owns the thread.
  const handleLogReply = (contact: RfpPrimeContact) =>
    setReplyTarget(contact.reply_contact_id ? { ...contact, id: contact.reply_contact_id } : contact);

  const handleReplySaved = (updated: RfpPrimeContact) => {
    handleFlagSaved(updated);
    setRefreshToken((t) => t + 1); // the saved row may not be the one displayed — reload the page
  };

  const handleFlagSaved = (updated: RfpPrimeContact) => {
    setItems((prev) => prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)));
  };

  const handleUnflag = async (contact: RfpPrimeContact) => {
    try {
      const updated = await setRfpPrimeContactHighValue(contact.id, false);
      handleFlagSaved(updated);
    } catch (e: any) {
      setError(e.response?.data?.detail || "Failed to unflag contact");
    }
  };

  const clearOpportunityFilter = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("opportunity_id");
      return next;
    });
  };

  const handleStatusSaved = (prime: PrimeContact) => {
    const email = statusChange?.contact.email;
    if (!email) return;
    const key = email.trim().toLowerCase();
    // Kept even when cleared back to active — reason/timestamp persist as
    // history, same as the backend never auto-clearing them.
    setStatusByEmail((prev) => ({
      ...prev,
      [key]: { status: prime.status, reason: prime.status_reason, status_at: prime.status_at },
    }));
  };

  return (
    <div className="flex flex-col gap-4 h-full min-h-0">
      {addOpen && (
        <AddPrimeContactModal onClose={() => setAddOpen(false)} onSaved={(created) => { setAddOpen(false); onAddContact(created); }} />
      )}
      <PageHeader
        title="Prime Contacts"
        description="Every company extracted from a decoded RFP's primes CSV, across every project — for LinkedIn (or any other) outreach. The ×N badge shows how many RFPs a company has appeared as a candidate on."
        actions={
          <>
            <Button variant="outline" size="sm" onClick={onShowNoRfp} title="People on file with no active RFP record, such as contacts added by hand">No RFP yet</Button>
            <Button variant="primary" size="sm" onClick={() => setAddOpen(true)} title="Add a contact person to a company by hand">
              <UserPlus className="w-3.5 h-3.5" /> Add contact
            </Button>
          </>
        }
      />

      {opportunityId && (
        <div className={`flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border ${TONE_SOFT.brand}`}>
          <span className="text-sm">
            Showing primes for: <span className="font-semibold">{opportunityTitle || `Opportunity #${opportunityId}`}</span>
          </span>
          <button
            onClick={clearOpportunityFilter}
            className="flex items-center gap-1 text-xs text-primary hover:underline"
          >
            <X className="w-3.5 h-3.5" /> View all
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            placeholder="Search prime name..."
            className={`w-56 pl-8 pr-3 py-1.5 h-9 ${FILLED_INPUT}`}
          />
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search company name..."
            className={`w-56 pl-8 pr-3 py-1.5 h-9 ${FILLED_INPUT}`}
          />
        </div>
        <select
          value={category}
          title="The company's status on the contact's own RFP (prime if any of its sheet rows is prime) — not the raw category text from the sheet"
          onChange={(e) => setCategory(e.target.value)}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Role on RFP: any</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>{ROLE_LABELS[c] ?? c}</option>
          ))}
        </select>
        <select
          value={planHolder}
          title="Whether the contact's company is a plan holder on the contact's RFP (CollabP sheet status)"
          onChange={(e) => setPlanHolder(e.target.value as "" | "true" | "false")}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Plan holder: any</option>
          <option value="true">Plan holders only</option>
          <option value="false">Not a plan holder</option>
        </select>
        <select
          value={state}
          onChange={(e) => setState(e.target.value)}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Office state: any</option>
          {stateOptions.map((st) => (
            <option key={st} value={st}>{st}</option>
          ))}
        </select>
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Office country: any</option>
          <option value="none">Not set</option>
          {countryOptions.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={hasEmail}
          onChange={(e) => setHasEmail(e.target.value as "" | "true" | "false")}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Email: any</option>
          <option value="true">Has email</option>
          <option value="false">No email</option>
        </select>
        <select
          value={hasLinkedin}
          onChange={(e) => setHasLinkedin(e.target.value as "" | "true" | "false")}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">LinkedIn: any</option>
          <option value="true">Has LinkedIn</option>
          <option value="false">No LinkedIn</option>
        </select>
        <select
          value={highValue}
          onChange={(e) => setHighValue(e.target.value as "" | "true" | "false")}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">High value: any</option>
          <option value="true">High value only</option>
          <option value="false">Not flagged</option>
        </select>
        <select
          value={replied}
          onChange={(e) => setReplied(e.target.value as "" | "true" | "false")}
          className={`px-3 py-1.5 h-9 ${FORM_CONTROL}`}
        >
          <option value="">Replied: any</option>
          <option value="true">Replied only</option>
          <option value="false">Not replied</option>
        </select>
        <span className="text-xs text-muted-foreground ml-auto">
          {totalCount} contact{totalCount === 1 ? "" : "s"}
        </span>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}

      <div className={`${TABLE_SHELL} overflow-x-auto flex-1 min-h-0`}>
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className={THEAD_ROW}>
              <th className={TH}>Contact</th>
              <th className={TH}>Company</th>
              <th className={TH}>Email</th>
              <th className={TH}>Phone</th>
              <th className={TH}>Location</th>
              <th className={TH}>LinkedIn</th>
              <th className={TH}>Outreach</th>
              {!opportunityId && <th className={TH}>Latest RFP</th>}
              <th className={TH}>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayedItems.map((c) => (
              <ContactRow
                key={c.id}
                contact={c}
                company={c.email ? companiesByEmail[c.email.trim().toLowerCase()] : undefined}
                status={c.email ? statusByEmail[c.email.trim().toLowerCase()] : undefined}
                showRfpColumn={!opportunityId}
                crmBasePath={crmBasePath}
                linkState={linkState}
                highlighted={!!c.prime_contact_id && highlightPrimeId === c.prime_contact_id}
                onFlag={setFlagTarget}
                onUnflag={handleUnflag}
                onLogReply={handleLogReply}
                onChangeStatus={(contact, target) => setStatusChange({ contact, target })}
              />
            ))}
            {!loading && displayedItems.length === 0 && (
              <tr>
                <td colSpan={opportunityId ? 8 : 9} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No contacts match these filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {loading && (
          <div className="flex items-center justify-center py-10">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>

      {!opportunityId && (
        <PagerNav page={page} totalPages={totalPages} onChange={setPage} />
      )}

      {flagTarget && (
        <FlagPrimeContactModal
          contact={flagTarget}
          onClose={() => setFlagTarget(null)}
          onSaved={handleFlagSaved}
        />
      )}

      {replyTarget && (
        <LogReplyModal
          contact={replyTarget}
          onClose={() => setReplyTarget(null)}
          onSaved={handleReplySaved}
        />
      )}

      {statusChange?.contact.email && (
        <SetPrimeContactStatusModal
          email={statusChange.contact.email}
          displayName={
            [statusChange.contact.first_name, statusChange.contact.last_name].filter(Boolean).join(" ") ||
            statusChange.contact.main_contact ||
            undefined
          }
          currentStatus={statusByEmail[statusChange.contact.email.trim().toLowerCase()]?.status ?? "active"}
          targetStatus={statusChange.target}
          onClose={() => setStatusChange(null)}
          onSaved={handleStatusSaved}
        />
      )}
    </div>
  );
}
